/* ESSNTL — Size Finder interaction.

   The maths lives in essntl-size-finder-engine.js. This file only collects the
   answers, renders what the engine returned, and hands the chosen size to the
   product page's own size buttons.

   The product's real sizes, stock and variant ids are read from the existing
   size grid rather than duplicated into Liquid, so the recommendation can never
   name a size the page does not sell, and the final CTA clicks the same button
   a customer would — which is what carries the variant, price, Add to Cart,
   sticky bar and Complete your rotation along with it.
*/
(function () {
  'use strict';

  var root = document.querySelector('[data-essntl-size-finder]');
  if (!root) return;

  var panel = root.querySelector('.essntl-sf__panel');
  var body = root.querySelector('[data-sf-body]');
  var steps = Array.prototype.slice.call(root.querySelectorAll('[data-sf-step]'));
  var backBtn = root.querySelector('[data-sf-back]');
  var nextBtn = root.querySelector('[data-sf-next]');
  var selectBtn = root.querySelector('[data-sf-select]');
  var restartBtn = root.querySelector('[data-sf-restart]');
  var measureBtn = root.querySelector('[data-sf-measurements]');
  var errorEl = root.querySelector('[data-sf-error]');
  var family = root.getAttribute('data-sf-family');

  var STORE_KEY = 'essntl_size_finder';
  var BOUNDS = {
    metric: { height: [140, 210], weight: [40, 160], hLabel: 'cm', wLabel: 'kg' },
    imperial: { height: [55, 83], weight: [88, 353], hLabel: 'in', wLabel: 'lb' }
  };

  var state = {
    units: 'metric',
    height: null, weight: null,
    chest: null, waist: null, hips: null,
    audience: 'unisex', build: 'regular', fit: 'as_designed',
    confirmed: false,
    step: 1,
    result: null
  };

  /* ── session memory: the answers, never the previous size ────── */
  try {
    var saved = JSON.parse(window.sessionStorage.getItem(STORE_KEY) || 'null');
    if (saved && saved.confirmed) {
      ['units', 'height', 'weight', 'chest', 'waist', 'hips', 'audience', 'build', 'fit']
        .forEach(function (k) { if (saved[k] !== undefined && saved[k] !== null) state[k] = saved[k]; });
      state.confirmed = true;
    }
  } catch (e) { /* private mode — the finder still works, it just forgets */ }

  function persist() {
    try {
      window.sessionStorage.setItem(STORE_KEY, JSON.stringify({
        units: state.units, height: state.height, weight: state.weight,
        chest: state.chest, waist: state.waist, hips: state.hips,
        audience: state.audience, build: state.build, fit: state.fit,
        confirmed: state.confirmed
      }));
    } catch (e) {}
  }

  /* ── the page's own size grid is the source of truth for sizes ── */
  function sizeButtons() {
    return Array.prototype.slice.call(document.querySelectorAll('.essntl-size-grid .essntl-size-btn'));
  }
  function productSizes() {
    return sizeButtons().map(function (b) { return (b.getAttribute('data-size') || '').trim(); })
      .filter(Boolean);
  }
  function stockMap() {
    var out = {};
    sizeButtons().forEach(function (b) {
      var label = (b.getAttribute('data-size') || '').trim();
      if (label) out[label] = !b.classList.contains('is-unavailable') && !b.disabled;
    });
    return out;
  }
  /* Shopify labels are not always the ladder's labels. */
  function normalise(label) {
    var v = String(label || '').trim().toUpperCase().replace(/\s|-/g, '');
    var map = { EXTRASMALL: 'XS', SMALL: 'S', MEDIUM: 'M', LARGE: 'L', EXTRALARGE: 'XL',
                XXL: 'XL', XXSMALL: 'XXS', EXTRAEXTRASMALL: 'XXS' };
    return map[v] || v;
  }
  function buttonFor(size) {
    var want = normalise(size);
    var found = null;
    sizeButtons().forEach(function (b) {
      if (!found && normalise(b.getAttribute('data-size')) === want) found = b;
    });
    return found;
  }

  /* ── step 1 controls ─────────────────────────────────────────── */
  var hNum = root.querySelector('[data-sf-height-number]');
  var hRange = root.querySelector('[data-sf-height-range]');
  var wNum = root.querySelector('[data-sf-weight-number]');
  var wRange = root.querySelector('[data-sf-weight-range]');

  function applyBounds() {
    var b = BOUNDS[state.units];
    [[hNum, hRange, b.height], [wNum, wRange, b.weight]].forEach(function (set) {
      set[0].min = set[2][0]; set[0].max = set[2][1];
      set[1].min = set[2][0]; set[1].max = set[2][1];
    });
    root.querySelector('[data-sf-height-unit]').textContent = b.hLabel;
    root.querySelector('[data-sf-weight-unit]').textContent = b.wLabel;
    root.querySelector('[data-sf-height-min]').textContent = b.height[0];
    root.querySelector('[data-sf-height-max]').textContent = b.height[1];
    root.querySelector('[data-sf-weight-min]').textContent = b.weight[0];
    root.querySelector('[data-sf-weight-max]').textContent = b.weight[1];
  }

  function midpoint(range) { return Math.round((range[0] + range[1]) / 2); }

  function syncStep1() {
    var b = BOUNDS[state.units];
    var h = state.height === null ? midpoint(b.height) : state.height;
    var w = state.weight === null ? midpoint(b.weight) : state.weight;
    hNum.value = round(h); hRange.value = h;
    wNum.value = round(w); wRange.value = w;
    hRange.setAttribute('aria-valuetext', round(h) + ' ' + b.hLabel);
    wRange.setAttribute('aria-valuetext', round(w) + ' ' + b.wLabel);
    root.querySelectorAll('[data-sf-units]').forEach(function (btn) {
      select(btn, btn.getAttribute('data-sf-units') === state.units);
    });
    root.querySelectorAll('[data-sf-audience]').forEach(function (btn) {
      select(btn, btn.getAttribute('data-sf-audience') === state.audience);
    });
    renderMeasures();
    updateNext();
  }

  function round(v) { return Math.round(v * 10) / 10; }
  function select(el, on) {
    el.classList.toggle('is-selected', on);
    el.setAttribute('aria-checked', on ? 'true' : 'false');
  }

  /* only the measurements this garment actually uses */
  function measureFields() {
    if (family === 'sweats' || family === 'shorts') return [['waist', 'Natural waist', 'around your natural waistline']];
    if (family === 'hoodie' || family === 'zip_hoodie') return [['chest', 'Chest', 'around the fullest part']];
    return [['chest', 'Chest', 'around the fullest part'],
            ['waist', 'Natural waist', 'around your natural waistline'],
            ['hips', 'Hips', 'around the fullest part']];
  }

  function renderMeasures() {
    var host = root.querySelector('[data-sf-measures]');
    if (host.getAttribute('data-built') === 'true') {
      host.querySelectorAll('input').forEach(function (i) {
        var k = i.getAttribute('data-sf-measure');
        i.value = state[k] === null ? '' : state[k];
      });
      host.querySelectorAll('[data-sf-measure-unit]').forEach(function (s) {
        s.textContent = state.units === 'metric' ? 'cm' : 'in';
      });
      return;
    }
    host.innerHTML = measureFields().map(function (f) {
      return '<label class="essntl-sf__measure">' +
             '<span class="essntl-sf__measure-label">' + f[1] +
             '<em class="essntl-sf__measure-hint">' + f[2] + '</em></span>' +
             '<span class="essntl-sf__measure-input">' +
             '<input type="number" inputmode="decimal" step="0.5" min="1" data-sf-measure="' + f[0] + '">' +
             '<span data-sf-measure-unit>cm</span></span></label>';
    }).join('');
    host.setAttribute('data-built', 'true');
    host.addEventListener('input', function (e) {
      var k = e.target.getAttribute && e.target.getAttribute('data-sf-measure');
      if (!k) return;
      var v = parseFloat(e.target.value);
      state[k] = (isFinite(v) && v > 0 && v < 400) ? v : null;
      persist();
    });
    renderMeasures();
  }

  function updateNext() {
    if (state.step !== 1) { nextBtn.disabled = false; return; }
    nextBtn.disabled = !state.confirmed;
  }

  function confirmValue(which, raw) {
    var b = BOUNDS[state.units][which];
    var v = parseFloat(raw);
    if (!isFinite(v)) return;
    v = Math.min(b[1], Math.max(b[0], v));
    state[which] = v;
    /* a value is only "confirmed" once the customer has set both deliberately */
    if (state.height !== null && state.weight !== null) state.confirmed = true;
    errorEl.textContent = '';
    syncStep1();
    persist();
  }

  [[hNum, 'height'], [hRange, 'height'], [wNum, 'weight'], [wRange, 'weight']].forEach(function (pair) {
    pair[0].addEventListener('input', function () { confirmValue(pair[1], pair[0].value); });
  });

  root.querySelectorAll('[data-sf-units]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      var to = btn.getAttribute('data-sf-units');
      if (to === state.units) return;
      /* convert what is there so the same body reads correctly in both systems */
      if (state.height !== null) state.height = to === 'imperial' ? state.height / 2.54 : state.height * 2.54;
      if (state.weight !== null) state.weight = to === 'imperial' ? state.weight / 0.45359237 : state.weight * 0.45359237;
      ['chest', 'waist', 'hips'].forEach(function (k) {
        if (state[k] !== null) state[k] = to === 'imperial' ? state[k] / 2.54 : state[k] * 2.54;
      });
      state.units = to;
      applyBounds(); syncStep1(); persist();
    });
  });

  root.querySelectorAll('[data-sf-audience]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.audience = btn.getAttribute('data-sf-audience');
      syncStep1(); persist();
    });
  });
  root.querySelectorAll('[data-sf-build]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.build = btn.getAttribute('data-sf-build');
      root.querySelectorAll('[data-sf-build]').forEach(function (b) { select(b, b === btn); });
      persist();
    });
  });
  root.querySelectorAll('[data-sf-fit]').forEach(function (btn) {
    btn.addEventListener('click', function () {
      state.fit = btn.getAttribute('data-sf-fit');
      root.querySelectorAll('[data-sf-fit]').forEach(function (b) { select(b, b === btn); });
      persist();
    });
  });

  /* ── steps ───────────────────────────────────────────────────── */
  function show(step) {
    state.step = step;
    steps.forEach(function (s) {
      s.hidden = parseInt(s.getAttribute('data-sf-step'), 10) !== step;
    });
    root.querySelectorAll('[data-sf-progress]').forEach(function (p) {
      var i = parseInt(p.getAttribute('data-sf-progress'), 10);
      p.classList.toggle('is-done', step > i || step === 4);
      p.classList.toggle('is-active', step === i);
      p.classList.toggle('is-complete', step === 4);
    });
    backBtn.hidden = step === 1;
    nextBtn.hidden = step === 4;
    selectBtn.hidden = step !== 4;
    restartBtn.hidden = step !== 4;
    measureBtn.hidden = step !== 4;
    nextBtn.textContent = step === 3 ? 'Show my size' : 'Continue';
    if (body) body.scrollTop = 0;
    updateNext();
  }

  nextBtn.addEventListener('click', function () {
    if (state.step === 1 && !state.confirmed) {
      errorEl.textContent = 'Set your height and weight to continue.';
      return;
    }
    if (state.step === 3) { compute(); show(4); return; }
    show(state.step + 1);
  });
  backBtn.addEventListener('click', function () { show(Math.max(1, state.step - 1)); });
  restartBtn.addEventListener('click', function () { show(1); });

  measureBtn.addEventListener('click', function () {
    /* the existing image Size Guide, not a second copy of it */
    close();
    var opener = document.querySelector('[data-open-size-guide]');
    if (opener) window.setTimeout(function () { opener.click(); }, 180);
  });

  /* ── result ──────────────────────────────────────────────────── */
  function compute() {
    var engine = window.ESSNTLSizeEngine;
    if (!engine) return;
    state.result = engine.recommend({
      family: family,
      units: state.units,
      height: state.height, weight: state.weight,
      chest: state.chest, waist: state.waist, hips: state.hips,
      build: state.build, fit: state.fit,
      availableSizes: productSizes().map(normalise),
      sizeAvailability: (function () {
        var raw = stockMap(), out = {};
        for (var k in raw) out[normalise(k)] = raw[k];
        return out;
      })()
    });
    render(state.result);
  }

  var STATUS = { best: 'Best match', close: 'Close match', between: 'Between sizes' };

  function render(r) {
    if (!r || !r.supported) return;
    root.querySelector('[data-sf-result-size]').textContent = r.size;
    var status = root.querySelector('[data-sf-result-status]');
    status.textContent = r.inStock ? STATUS[r.confidence] : r.size + ' is currently unavailable';
    status.classList.toggle('is-warn', !r.inStock);

    root.querySelector('[data-sf-result-copy]').textContent = summary(r);

    var altEl = root.querySelector('[data-sf-result-alt]');
    if (r.alternative) {
      altEl.hidden = false;
      altEl.textContent = altCopy(r);
    } else { altEl.hidden = true; }

    root.querySelector('[data-sf-breakdown]').innerHTML = r.components.map(function (c) {
      return '<div class="essntl-sf__part">' +
        '<div class="essntl-sf__part-head"><span>' + esc(c.label) + '</span>' +
        '<strong>Size ' + esc(c.size) + '</strong></div>' +
        '<ul class="essntl-sf__part-list">' + lines(c).map(function (l) {
          return '<li>' + esc(l) + '</li>';
        }).join('') + '</ul></div>';
    }).join('');

    var model = root.querySelector('[data-sf-result-model]');
    var withModel = null;
    r.components.forEach(function (c) {
      c.reasons.forEach(function (x) { if (x.key === 'model') withModel = x; });
    });
    if (withModel) {
      model.hidden = false;
      model.textContent = 'For scale: our model is ' + withModel.height + ' cm and wears ' +
        withModel.size + ' in the photos.';
    } else { model.hidden = true; }

    var btn = buttonFor(r.size);
    selectBtn.disabled = !btn || !r.inStock;
    selectBtn.textContent = btn && r.inStock
      ? (btn.classList.contains('is-active') ? 'Keep size ' + r.size : 'Select size ' + r.size)
      : 'Size ' + r.size + ' unavailable';
    selectBtn.setAttribute('data-size', r.size);
  }

  /* Every sentence below is assembled from engine output. Nothing is asserted
     that the recommendation did not actually compute. */
  function summary(r) {
    var parts = r.components;
    if (r.conflict && parts.length === 2) {
      return 'Your ' + parts[0].label.toLowerCase() + ' leans ' + parts[0].ownSize +
             ' and your ' + parts[1].label.toLowerCase() + ' leans ' + parts[1].ownSize +
             '. ' + r.size + ' is the better single size for the set — it keeps the ' +
             (sizeIndex(r, parts[1].ownSize) > sizeIndex(r, parts[0].ownSize) ? parts[1].label.toLowerCase() : parts[0].label.toLowerCase()) +
             ' comfortable while the rest stays as designed.';
    }
    var chest = firstMeasure(parts, 'chest');
    if (chest && chest.ease > 0) {
      return r.size + ' leaves about ' + Math.round(chest.ease) + ' cm of room around the chest — ' +
             'that is the intended ESSNTL fit, measured against the garment.';
    }
    var waist = firstMeasure(parts, 'waist');
    if (waist && waist.ease !== undefined) {
      return r.size + ' matches your waist against the garment with room to sit comfortably.';
    }
    return r.size + ' is the closest match to the intended ESSNTL fit for your height and build.';
  }

  function altCopy(r) {
    var a = r.alternative;
    if (a.reason === 'unavailable') {
      return a.size + ' is in stock and sits ' + (a.direction === 'looser' ? 'looser' : 'closer to the body') + '.';
    }
    if (a.reason === 'between') return 'You sit close to ' + a.size + ' as well — go ' + a.size + ' for a touch less volume.';
    return 'Prefer a closer fit? ' + a.size + ' could also work.';
  }

  function lines(c) {
    var out = [];
    c.measurements.forEach(function (m) {
      if (m.key === 'chest' && m.ease !== undefined) {
        out.push('Chest — about ' + Math.round(m.ease) + ' cm of room' + (c.intent === 'oversized' ? ', as designed' : ''));
      }
      if (m.key === 'waist') {
        out.push('Waist — ' + (m.stretchTo ? 'sits ' + m.garmentGirth + '–' + m.stretchTo + ' cm' : m.garmentGirth + ' cm relaxed'));
      }
      if (m.key === 'length') out.push('Length — ' + m.value + ' cm' + (c.lengthNote === 'short' ? ', shorter on you' : c.lengthNote === 'long' ? ', longer on you' : ''));
      if (m.key === 'leg_opening') out.push('Leg opening — ' + m.value + ' cm');
    });
    return out;
  }

  function firstMeasure(parts, key) {
    var found = null;
    parts.forEach(function (p) {
      p.measurements.forEach(function (m) { if (!found && m.key === key) found = m; });
    });
    return found;
  }
  function sizeIndex(r, s) { return r.ladder.indexOf(s); }
  function esc(v) {
    return String(v).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* ── selecting the size for real ─────────────────────────────── */
  selectBtn.addEventListener('click', function () {
    var r = state.result;
    if (!r) return;
    var btn = buttonFor(r.size);
    if (!btn || btn.classList.contains('is-unavailable')) return;
    /* the page's own button, so variant, price, Add to Cart, the sticky bar and
       Complete your rotation all update exactly as they do for a real click */
    btn.click();
    close();
  });

  /* ── open / close ────────────────────────────────────────────── */
  var lastFocus = null, scrollY = 0;

  function open() {
    lastFocus = document.activeElement;
    scrollY = window.scrollY || window.pageYOffset || 0;
    root.setAttribute('aria-hidden', 'false');
    root.classList.add('is-open');
    document.documentElement.classList.add('essntl-sf-locked');
    document.body.style.top = '-' + scrollY + 'px';
    applyBounds();
    syncStep1();
    root.querySelectorAll('[data-sf-build]').forEach(function (b) { select(b, b.getAttribute('data-sf-build') === state.build); });
    root.querySelectorAll('[data-sf-fit]').forEach(function (b) { select(b, b.getAttribute('data-sf-fit') === state.fit); });
    show(1);
    window.setTimeout(function () { panel.focus(); }, 30);
    document.addEventListener('keydown', onKey, true);
  }

  function close() {
    root.setAttribute('aria-hidden', 'true');
    root.classList.remove('is-open');
    document.documentElement.classList.remove('essntl-sf-locked');
    document.body.style.top = '';
    window.scrollTo(0, scrollY);
    document.removeEventListener('keydown', onKey, true);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function onKey(e) {
    if (e.key === 'Escape') { e.preventDefault(); close(); return; }
    if (e.key !== 'Tab') return;
    var f = Array.prototype.slice.call(panel.querySelectorAll(
      'button:not([hidden]):not([disabled]), input:not([disabled]), summary, [href], [tabindex]:not([tabindex="-1"])'
    )).filter(function (el) { return el.offsetParent !== null; });
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  root.querySelectorAll('[data-sf-close]').forEach(function (el) {
    el.addEventListener('click', close);
  });

  document.querySelectorAll('[data-open-size-finder]').forEach(function (el) {
    el.addEventListener('click', function (e) { e.preventDefault(); open(); });
  });
})();
